import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import InfoDialogBox from 'components/InfoDialogBox'
import * as Yup from 'yup'
import { useIntl, useDebounceSearch } from 'hooks'
import { validationSchema } from 'schema/addTag'
import {
    useGetTagsSuggestionsQuery,
    useAddTagMutation,
    useGetTagsAggregateQuery,
    useDeleteTagMutation,
} from 'services'
import { generateAllForPayload, generateAllForQuery } from 'utils/helpers'
import Searchbar from 'features/searchbar/Searchbar'
import Modal from 'components/Modals'
import { Pill } from 'components/Zui'
import './index.css'

function Index({ onClose, data: tagData }) {
    const { t } = useIntl()
    const [errors, setErrors] = useState({})
    const dispatch = useDispatch()
    const { data } = useGetTagsAggregateQuery(generateAllForQuery(tagData), {
        skip: !tagData,
    })
    const [addTag] = useAddTagMutation()
    const [deleteTag] = useDeleteTagMutation()
    const {
        isTyping,
        setKeyword,
        keyword,
        data: suggestion,
        isFetching,
        isError,
    } = useDebounceSearch(useGetTagsSuggestionsQuery, {
        min: 3,
    })

    const handleAddNewTag = useCallback(
        async v => {
            const schema = Yup.object(validationSchema(t))
            const user = await schema.isValid({
                tag: keyword,
            })

            if (!user) {
                schema
                    .validate(
                        {
                            tag: keyword,
                        },
                        { abortEarly: false }
                    )
                    .catch(err => {
                        const errors = err.inner.reduce((acc, error) => {
                            return {
                                ...acc,
                                [error.path]: error.message,
                            }
                        }, {})
                        setErrors(errors)
                    })
            } else {
                setErrors({})
                const payload = {
                    body: { ...generateAllForPayload(tagData), tag: keyword },
                    query: generateAllForQuery(tagData),
                }
                addTag(payload)
                setKeyword('')
            }
        },
        [dispatch, keyword]
    )

    const handleChange = v => {
        setErrors({ tag: '' })
        setKeyword(v)
    }

    const handleDeletePill = tag => {
        const payload = {
            body: { ...generateAllForPayload(tagData), tag },
            query: generateAllForQuery(tagData),
        }
        deleteTag(payload)
    }

    const handleSelectSuggestion = value => {
        const payload = {
            body: { ...generateAllForPayload(tagData), tag: value.name },
            query: generateAllForQuery(tagData),
        }
        addTag(payload)
    }

    return (
        <Modal>
            <InfoDialogBox headerText="Tags" onClose={onClose} buttonText="Done">
                <div className="tags-details-popup-content">
                    <div className="add-tag-form-wrapper">
                        <div className="input-search-tag">
                            <Searchbar
                                data={suggestion}
                                onChange={handleChange}
                                classes={['fullwidth-input']}
                                onEnter={handleAddNewTag}
                                onSelectSuggestion={handleSelectSuggestion}
                                isTyping={(isTyping || isFetching) && !isError}
                                validationError={errors.tag}
                                icon={false}
                                value={keyword}
                                reinitialize
                                placeholder={t('add.tag')}
                                button={t('add')}
                            />
                        </div>
                    </div>
                    {tagData.type === 'files' && tagData.fileIds.length > 1 ? (
                        <div className="tag-types-wrapper">
                            {data?.all.length > 0 && (
                                <div className="tags-pills-wrapper">
                                    <div className="tags-type-wrapper">
                                        <div className="tags-type-title">
                                            <span>{t('applied.all')}</span>
                                        </div>
                                        <div className="popup-tags-list-wrapper">
                                            {data.all.map(tag => (
                                                <Pill
                                                    key={tag}
                                                    closable
                                                    onClose={() => handleDeletePill(tag)}
                                                >
                                                    {tag}
                                                </Pill>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {data?.any.length > 0 && (
                                <div className="tags-pills-wrapper">
                                    <div className="tags-type-wrapper">
                                        <div className="tags-type-title">
                                            <span>{t('applied.some')}</span>
                                        </div>
                                        <div className="popup-tags-list-wrapper">
                                            {data.any.map(tag => (
                                                <Pill
                                                    key={tag}
                                                    closable
                                                    onClose={() => handleDeletePill(tag)}
                                                >
                                                    {tag}
                                                </Pill>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="tags-pills-wrapper">
                            <div className="tags-type-wrapper">
                                <div className="popup-tags-list-wrapper">
                                    {data &&
                                        [...data.any, ...data.all].length > 0 &&
                                        [...data.any, ...data.all].map(tag => (
                                            <Pill
                                                key={tag}
                                                closable
                                                onClose={() => handleDeletePill(tag)}
                                            >
                                                {tag}
                                            </Pill>
                                        ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </InfoDialogBox>
        </Modal>
    )
}

export default Index
